import { useMemo } from 'react';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { UseFormatValues } from 'common/types/format';
import { CustomScalarSpreadsheet } from 'components';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SLUG } from 'pages/ValuationsAllocation/common/constants/weightedShareValues';
import { getObjectValue, getStringValue } from 'utilities';
import useStyles from './styles';
import { FundSharesProps } from './types';

const ALLOCATION_WEIGHTED_SHARE_VALUES_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SLUG}-container`;

const FundShares = ({
  isLoading,
  isLoadingValuationSummary,
  isSaving,
  onSpreadsheetChange,
  weightedShareValuesSpreadsheet,
  workbook,
}: FundSharesProps) => {
  const { data: weightedShareValuesSpreadsheetData } = getObjectValue(weightedShareValuesSpreadsheet);

  const classes = useStyles();

  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  // Handle transition from GridSkeleton to Weighted Share Values
  // Updated logic to handle empty spreadsheet due to no fund ownership details or only loans
  const isLoadingWeightedShareValues = useMemo(() => {
    // If we're still loading the main data, show skeleton
    if (isLoading || isLoadingValuationSummary) {
      return true;
    }
    
    // If the spreadsheet object doesn't exist yet, we're still loading
    if (!weightedShareValuesSpreadsheet) {
      return true;
    }
    
    // If we have the spreadsheet object but no data, check if it's because there's no data to display
    // vs still loading. We can determine this by checking if the spreadsheet has been initialized
    // but has empty data (which could mean no fund ownership details or only loans)
    if (weightedShareValuesSpreadsheet && isEmpty(weightedShareValuesSpreadsheetData)) {
      // If the spreadsheet exists but has no data, it might be because there's no data to display
      // rather than still loading. We should show the empty spreadsheet instead of skeleton.
      return false;
    }
    
    return false;
  }, [isLoading, isLoadingValuationSummary, weightedShareValuesSpreadsheet, weightedShareValuesSpreadsheetData]);

  // Create Fund Shares Sheet props
  const fundSharesSheetProps = useMemo(() => {
    if (weightedShareValuesSpreadsheet) {
      return {
        ...weightedShareValuesSpreadsheet,
        format,
        formatDispatch,
        onChange: onSpreadsheetChange,
        sheet: weightedShareValuesSpreadsheet,
        workbook,
      } as unknown as typeof ScalarSpreadsheet;
    }

    return null;
  }, [format, formatDispatch, onSpreadsheetChange, weightedShareValuesSpreadsheet, workbook]);

  // Extract key for direct JSX prop
  const spreadsheetKey = useMemo(() => getStringValue(weightedShareValuesSpreadsheet?.name), [weightedShareValuesSpreadsheet]);

  // Loading Weighted Share Values
  if (isLoadingWeightedShareValues) {
    return (
      <Grid className={clsx(classes.container, ALLOCATION_WEIGHTED_SHARE_VALUES_CONTAINER_CLASS)}>
        <GridSkeleton />
      </Grid>
    );
  }

  return (
    <Grid className={clsx(classes.container, ALLOCATION_WEIGHTED_SHARE_VALUES_CONTAINER_CLASS)}>
      <CustomScalarSpreadsheet
        key={spreadsheetKey}
        {...fundSharesSheetProps}
        isLoading={isLoading}
        isSaving={isSaving}
      />
    </Grid>
  );
};

export default FundShares;
